// src/components/Phaser/help/SoundHelp.tsx
import React, { useRef, useState } from 'react';
import { Button, Col, Modal, Row, Typography, message } from 'antd';
import { CopyOutlined, CheckOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { TSound } from '../../../../models/objects/media';
import { getSoundPath } from '../../../../utils/format/valueFormat';

const { Title, Paragraph, Text } = Typography;

type Language = 'javascript' | 'python';

interface Props {
  sounds: TSound[];
  open: boolean;
  onClose: () => void;
  language: Language; // 👈 ajoute le langage courant
}

function makeGlobalSnippet(language: Language) {
  if (language === 'python') {
    return {
      display: `# Joue un son
playSound("sonName")`,
      copy: `# Joue un son
playSound("sonName")`,
    };
  }
  return {
    display: `// Joue un son
playSound("sonName");`,
    copy: `// Joue un son
playSound("sonName");`,
  };
}

function makePlaySnippet(name: string, language: Language) {
  return language === 'python' ? `playSound("${name}")` : `playSound("${name}");`;
}

const SoundHelp: React.FC<Props> = ({ sounds, open, onClose, language }) => {
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const [copiedName, setCopiedName] = useState<string | null>(null);
  const [globalCopied, setGlobalCopied] = useState(false);

  /* preview 5s ------------------------------------------------------------ */
  const playPreview = (snd: TSound) => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current.currentTime = 0;
    }
    const a = new Audio(getSoundPath(snd.file_path));
    previewRef.current = a;
    a.play();
    setTimeout(() => {
      if (previewRef.current === a) {
        a.pause();
        a.currentTime = 0;
      }
    }, 5000);
  };

  /* copy helpers ---------------------------------------------------------- */
  const { display: globalDisplay, copy: globalCopy } = makeGlobalSnippet(language);

  const copyGlobalSnippet = () => {
    navigator.clipboard.writeText(globalCopy);
    setGlobalCopied(true);
    message.success('Code copié !');
    setTimeout(() => setGlobalCopied(false), 2000);
  };

  const copySnippet = (name: string) => {
    navigator.clipboard.writeText(makePlaySnippet(name, language));
    setCopiedName(name);
    message.success('Code copié !');
    setTimeout(() => setCopiedName(null), 2000);
  };

  /* render ---------------------------------------------------------------- */
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="90%"
      centered
      styles={{
        body: {
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
          padding: 12,
        },
        content: { margin: 0 },
      }}
    >
      <Title level={3}>Sons</Title>

      {/* bloc générique */}
      <div
        style={{
          background: '#fafafa',
          padding: 16,
          borderRadius: 8,
          marginBottom: 24,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Paragraph>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            <code>{globalDisplay}</code>
          </pre>
        </Paragraph>
        <Button
          icon={globalCopied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={copyGlobalSnippet}
        >
          {globalCopied ? 'Copié' : 'Copier'}
        </Button>
      </div>

      {/* cartes sons */}
      <Row gutter={[24, 24]} justify="start">
        {sounds.map((snd) => {
          const soundName = snd.file_name.replace(/\.[^/.]+$/, ''); // nom sans extension
          return (
            <Col key={snd.id} xs={12} sm={8} md={6} lg={4} xl={3}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  backgroundColor: '#fff',
                  padding: 12,
                  borderRadius: 8,
                }}
              >
                <Text strong style={{ marginBottom: 4 }}>{soundName}</Text>

                <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                  <Button icon={<PlayCircleOutlined />} size="small" onClick={() => playPreview(snd)} />
                  <Button
                    icon={copiedName === soundName ? <CheckOutlined /> : <CopyOutlined />}
                    size="small"
                    onClick={() => copySnippet(soundName)}
                  />
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </Modal>
  );
};

export default SoundHelp;
